/* eslint-disable jsx-a11y/no-static-element-interactions */
import Button from '@/components/Button';
import { SelectForm } from './Form';
import { PlusCircleIcon } from '@heroicons/react/20/solid';
import { Input } from '@/components/ui/input';
import { useProfileInfo } from './useProfileInfo';
import { buildLastSignInSummary } from './lastSignIn';
import classNames from 'classnames';
import { useId } from 'react';

const ROUNDED_HEXAGON_PATH =
  'M 21.25 11.75 Q 25 5, 32.5 5 L 67.5 5 Q 75 5, 78.75 12.75 L 96.25 43.25 Q 100 50, 96.25 56.75 L 78.75 88.25 Q 75 95, 67.5 95 L 32.5 95 Q 25 95, 21.25 88.25 L 3.75 56.75 Q 0 50, 3.75 43.25 L 21.25 11.75 Z';

export default function ProfileInfo() {
  const {
    user,
    isEditing,
    response,
    isLoading,
    forename,
    surname,
    fileRef,
    location,
    profile,
    sourcePhoto,
    page,
    register,
    onClickProfile,
    onClickProfilePicture,
    onChangeFile,
    onSaveChanges,
    convertDate,
  } = useProfileInfo();

  const {
    roleTitle = 'Role',
    memberSinceTitle = 'Member since',
    youLocationTitle = 'Location',
    editYourInfoCta = 'Edit your info',
    saveYourChangesCta = 'Save your changes',
    forenamePlaceholder = 'forename',
    surnamePlaceholder = 'surname',
  } = page?.profileInfoSection ?? {};

  // These CMS fields were added later, so an entry saved before they existed
  // returns `null` (which a destructuring default would not catch) — use `??`
  // so the sensible defaults apply for both null and undefined.
  const lastSignInTitle = page?.profileInfoSection?.lastSignInTitle ?? 'Last sign-in';
  const unrecognisedActivityMessage =
    page?.profileInfoSection?.unrecognisedActivityMessage ??
    "Don't recognise this activity? Change your password below and contact your administrator.";

  const displayName = `${forename || user?.forename || 'Guest'} ${(surname || user?.surname) ?? ''}`.trim();

  // Prefer the previous session — the meaningful "was this you last time?" signal
  // and the context where the "unrecognised activity" warning belongs. On a first
  // ever sign-in there is no previous session, so fall back to the current login
  // so the block still shows real data instead of an awkward placeholder.
  const previousSignInSummary = buildLastSignInSummary({
    at: user?.previousLoginAt,
    ip: user?.previousLoginIp,
    userAgent: user?.previousLoginUserAgent,
  });
  const currentSignInSummary = buildLastSignInSummary({
    at: user?.lastLoginAt,
    ip: user?.lastLoginIp,
    userAgent: user?.lastLoginUserAgent,
  });
  const isPreviousSession = Boolean(previousSignInSummary);
  const lastSignInSummary = previousSignInSummary || currentSignInSummary;

  const renderViewState = () => {
    return (
      <div className="flex flex-1 flex-col gap-[13px]">
        <h1 className="font-haffer text-sh-lg font-bold text-black-200">{displayName}</h1>

        <div className="flex flex-col gap-[8px] md:flex-row md:gap-[25px]">
          <div className="flex flex-1 flex-col gap-[8px]">
            <span className="font-haffersq text-sh-xs font-normal text-dark-grey">{roleTitle}</span>
            <span className="font-haffersq text-sh-xs font-normal text-black">
              {profile || user?.profile?.toString() || '-'}
            </span>
          </div>

          <div className="flex flex-1 flex-col gap-[8px]">
            <span className="font-haffersq text-sh-xs font-normal text-dark-grey">{memberSinceTitle}</span>
            <span className="font-haffersq text-sh-xs font-normal text-black">
              {convertDate(user?.createdAt) || '-'}
            </span>
          </div>

          <div className="flex flex-1 flex-col gap-[8px]">
            <span className="font-haffersq text-sh-xs font-normal text-dark-grey">{youLocationTitle}</span>
            <span className="font-haffersq text-sh-xs font-normal text-black">{location || user?.location || '-'}</span>
          </div>
        </div>

        {lastSignInSummary && (
          <div className="flex flex-col gap-[8px]">
            <span className="font-haffersq text-sh-xs font-normal text-dark-grey">{lastSignInTitle}</span>
            <span className="font-haffersq text-sh-xs font-normal text-black">{lastSignInSummary}</span>
            {isPreviousSession && (
              <span className="font-haffersq text-[11px] font-normal text-dark-grey">
                {unrecognisedActivityMessage}
              </span>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onSaveChanges}
          className="w-fit font-haffer text-sh-xs font-bold !text-people-teal-600 hover:underline">
          {editYourInfoCta}
        </button>
      </div>
    );
  };

  const renderFormState = () => {
    return (
      <div className="flex flex-col gap-5">
        <SelectForm response={{ ...response }} onClickProfile={onClickProfile} defaultValues={{ role: profile }} />
        <div className="w-full md:w-2/3">
          <Input type="text" placeholder={forenamePlaceholder} {...register('forename')} />
        </div>
        <div className="w-full md:w-2/3">
          <Input type="text" placeholder={surnamePlaceholder} {...register('surname')} />
        </div>
        <Button theme="solid" onClick={onSaveChanges} loading={isLoading} className="w-fit">
          {saveYourChangesCta}
        </Button>
      </div>
    );
  };

  const maskId = useId().replace(/:/g, '-');
  const hexagonSizeClasses = 'h-[140px] w-[140px]';
  const fileInputClasses = 'absolute inset-0 cursor-pointer opacity-0';

  return (
    <div className="flex flex-col items-start gap-[16px] md:flex-row md:items-start md:gap-[16px]">
      <svg width={0} height={0} aria-hidden>
        <defs>
          <clipPath id={maskId} clipPathUnits="objectBoundingBox">
            <path d={ROUNDED_HEXAGON_PATH.replace(/[\d.]+/g, (m) => (Number(m) * 0.01).toString())} />
          </clipPath>
        </defs>
      </svg>
      <div className="flex shrink-0 items-center justify-center p-[16px]">
        <div
          className={classNames('relative overflow-hidden', hexagonSizeClasses)}
          style={{ clipPath: `url(#${maskId})`, WebkitClipPath: `url(#${maskId})` }}>
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              width: 280,
              height: 280,
              clipPath: `url(#${maskId})`,
              WebkitClipPath: `url(#${maskId})`,
              transform: 'scale(0.5)',
            }}>
            {sourcePhoto && (
              <img
                src={sourcePhoto}
                alt="profile"
                className="absolute inset-0 h-full w-full object-cover object-center"
                style={{ transform: 'scale(1.03)' }}
                decoding="async"
                fetchPriority="high"
              />
            )}
          </div>
          <div
            className={classNames(
              'absolute inset-0 flex items-center justify-center font-bold uppercase text-black-100 shadow-[0_0.72px_2.88px_rgba(0,0,0,0.25)]',
              {
                'text-sh-32-40 border-[4px] border-white bg-gradient-to-br from-[#B7DFFA] to-[#CFF6D8]': !sourcePhoto,
              },
            )}>
            {!sourcePhoto && (
              <>
                {`${user?.forename ? user?.forename?.toString()[0] : ''}${user?.surname ? user?.surname?.toString()[0] : ''}`}
                {`${!user?.forename && !user?.surname ? 'G' : ''}`}
              </>
            )}
          </div>
          {isEditing && (
            <>
              <input
                type="file"
                ref={fileRef}
                accept="image/*"
                onChange={onChangeFile}
                className={classNames(fileInputClasses, 'absolute inset-0 h-full w-full')}
              />
              <div
                className="absolute inset-0 cursor-pointer bg-black opacity-50"
                onClick={onClickProfilePicture}
                aria-hidden>
                <PlusCircleIcon className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 transform font-bold text-white" />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex-1">{isEditing ? renderFormState() : renderViewState()}</div>
    </div>
  );
}
